<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import AddInsightsDomainWithParser from '../addInsights/domainWithParser.vue';
import { _mft } from '../../i18n.js';
import { useNotificationStore } from '@/stores/NotificationStore.js'
const notificationStore = useNotificationStore()

const props = defineProps({
    searchId: { type: Number, required: true },
    searchName: { type: String, default: '' },
});

const emit = defineEmits(['back']);

const leads = ref([]);
const pendingResults = ref(0);
const loading = ref(true);
const filter = ref('all');
const insightsDomain = ref(null);
const insightsEmail = ref(null);
const budget = ref(null);
const collectCount = ref(10);
const collecting = ref(false);
const pendingPollTimer = ref(null);
const unmounted = ref(false);

const isFirstCollection = computed(() => {
    return leads.value.length === 0 && pendingResults.value === 0;
});

const hasBudget = computed(() => {
    return budget.value && budget.value.max_leads > 0;
});

const budgetPercent = computed(() => {
    if (!budget.value) return 0;
    return Math.round((budget.value.remaining_budget / budget.value.monthly_limit) * 100);
});

const acquiredLeadsCount = computed(() => {
    if (!budget.value) return 0;

    return Math.max(0, budget.value.included_leads - budget.value.max_leads);
});

const usagePercent = computed(() => {
    if (!budget.value || budget.value.max_leads === 0) return 100;
    return Math.round((collectCount.value * budget.value.cost_per_lead / budget.value.monthly_limit) * 100);
});

const filteredLeads = computed(() => {
    if (filter.value === 'not_added') return leads.value.filter(l => !l.in_crm && !l.hidden);
    if (filter.value === 'added') return leads.value.filter(l => l.in_crm);
    if (filter.value === 'hidden') return leads.value.filter(l => l.hidden);
    return leads.value.filter(l => !l.hidden);
});

const counts = computed(() => ({
    all: leads.value.filter(l => !l.hidden).length,
    not_added: leads.value.filter(l => !l.in_crm && !l.hidden).length,
    added: leads.value.filter(l => l.in_crm).length,
    hidden: leads.value.filter(l => l.hidden).length,
}));

const filterItems = computed(() => [
    { value: 'all', label: _mft('lead-hunter:scout.filterAll'), count: counts.value.all },
    { value: 'not_added', label: _mft('lead-hunter:scout.filterNotAdded'), count: counts.value.not_added },
    { value: 'added', label: _mft('lead-hunter:scout.filterAdded'), count: counts.value.added },
    { value: 'hidden', label: _mft('lead-hunter:scout.filterHidden'), count: counts.value.hidden },
]);

const estimatedTime = computed(() => {
    if (!budget.value?.avg_seconds_per_lead) return null;
    const totalSeconds = Math.round(collectCount.value * budget.value.avg_seconds_per_lead);
    if (totalSeconds < 60) return totalSeconds + 's';
    const minutes = Math.round(totalSeconds / 60);
    return minutes + ' min';
});

const stopPendingPoll = () => {
    if (pendingPollTimer.value) {
        clearInterval(pendingPollTimer.value);
        pendingPollTimer.value = null;
    }
};

const startPendingPoll = () => {
    if (pendingPollTimer.value) return;

    pendingPollTimer.value = setInterval(() => {
        loadLeads({ showLoading: false });
    }, 5000);
};

const updatePendingPoll = () => {
    if (unmounted.value) return;

    if (pendingResults.value > 0) {
        startPendingPoll();
        return;
    }

    stopPendingPoll();
};

const loadLeads = async ({ showLoading = true } = {}) => {
    if (showLoading) {
        loading.value = true;
    }

    try {
        const response = await fetch(route('lead-searches.show', { id: props.searchId }));
        const data = await response.json();

        if (unmounted.value) {
            return;
        }

        leads.value = data.leads || [];
        pendingResults.value = data.pending_results || 0;
        updatePendingPoll();
    } catch (e) {
        console.error('Failed to load leads', e);
    } finally {
        if (showLoading) {
            loading.value = false;
        }
    }
};

watch(() => props.searchId, () => {
    stopPendingPoll();
    filter.value = 'all';
    insightsDomain.value = null;
    insightsEmail.value = null;
    loadLeads();
});

const loadBudget = async () => {
    try {
        const response = await fetch(route('lead-searches.budget'));
        budget.value = await response.json();
        if (budget.value.max_leads > 0) {
            collectCount.value = budget.value.default_leads || Math.min(10, budget.value.max_leads);
        }
    } catch (e) {
        console.error('Failed to load budget', e);
    }
};

const collectLeads = async () => {
    if (collecting.value || !hasBudget.value) return;
    collecting.value = true;
    try {
        const response = await fetch(route('lead-searches.collect', { searchId: props.searchId }), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
            },
            body: JSON.stringify({ count: collectCount.value }),
        });
        if (response.ok) {
            await loadBudget();
            await loadLeads();
        } else {
            const data = await response.json();
            notificationStore.addNotification({
                type: 'error',
                title: _mft('error:error.generalOccurred'), // Title of the notification
                message: data.error || 'Collection failed',
                dismissAfter: 5000,
            })
        }
    } catch (e) {
        console.error('Failed to collect', e);
    } finally {
        collecting.value = false;
    }
};

const onLeadAdded = (data) => {
    const lead = leads.value.find(l => l.id === data.leadId);
    if (lead) {
        lead.in_crm = true;
        lead.lead_id = data.crmLeadId;
        lead.crm_url = data.crmUrl;
        lead.email = data.email;
    }
};

const hideLead = async (leadId) => {
    try {
        const response = await fetch(route('lead-searches.hide-lead', { searchId: props.searchId, leadId }), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
            },
        });
        if (response.ok) {
            const data = await response.json();
            const lead = leads.value.find(l => l.id === leadId);
            if (lead) lead.hidden = data.hidden;
        }
    } catch (e) {
        console.error('Failed to hide lead', e);
    }
};

const cleanDomain = (domain) => {
    if (!domain || domain === 'Domain not found') return null;
    try {
        if (domain.includes('://')) return new URL(domain).hostname.replace(/^www\./, '');
        return domain.replace(/^www\./, '');
    } catch {
        return domain;
    }
};

const showInsights = (lead) => {
    insightsDomain.value = cleanDomain(lead.domain);
    insightsEmail.value = lead.email;
};

const closeInsights = () => {
    insightsDomain.value = null;
    insightsEmail.value = null;
};

onMounted(() => {
    loadLeads();
    loadBudget();
});

onUnmounted(() => {
    unmounted.value = true;
    stopPendingPoll();
});
</script>

<template>
    <div class="flex flex-col h-full">
        <!-- Header -->
        <div class="px-4 py-3 border-b border-gray-200 bg-white">
            <div class="flex items-center justify-between mb-2">
                <div class="flex items-center gap-3">
                    <button @click="emit('back')" class="p-1 rounded hover:bg-gray-100 text-gray-400" :title="_mft('lead-hunter:scout.backToScout')">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
                        </svg>
                    </button>
                    <h2 class="text-sm font-semibold text-gray-900 mb-0">{{ searchName }}</h2>
                </div>
            </div>
            <!-- Stats -->
            <div v-if="!loading" class="flex items-center gap-4 text-[10px]">
                <div class="flex items-center gap-1">
                    <span class="w-2 h-2 rounded-full bg-blue-400"></span>
                    <span class="text-gray-500">{{ counts.all }} {{ _mft('lead-hunter:scout.statsTotal') }}</span>
                </div>
                <div class="flex items-center gap-1">
                    <span class="w-2 h-2 rounded-full bg-green-400"></span>
                    <span class="text-gray-500">{{ counts.added }} {{ _mft('lead-hunter:scout.statsInCrm') }}</span>
                </div>
                <div class="flex items-center gap-1">
                    <span class="w-2 h-2 rounded-full bg-gray-300"></span>
                    <span class="text-gray-500">{{ counts.not_added }} {{ _mft('lead-hunter:scout.statsNew') }}</span>
                </div>
                <div v-if="pendingResults > 0" class="flex items-center gap-1">
                    <span class="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
                    <span class="text-amber-600">{{ _mft('lead-hunter:scout.statsCollecting') }}</span>
                </div>
            </div>
        </div>

        <!-- Collection controls — always visible -->
        <div v-if="budget && !loading" class="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <!-- No budget left -->
            <div v-if="!hasBudget" class="text-center py-1">
                <p class="text-xs text-gray-500 mb-0 font-bold">
                    <i class="fa-regular fa-lock text-gray-400 mr-1"></i>
                    {{ _mft('lead-hunter:scout.noBudget') }}
                </p>
            </div>

            <!-- First collection: slider -->
            <template v-else-if="isFirstCollection && pendingResults === 0">
                <div class="flex items-center justify-between mb-2">
                    <span class="text-xs font-medium text-gray-700">{{ _mft('lead-hunter:scout.howManyLeads') }}</span>
                </div>
                <div class="flex items-center gap-3 mb-2">
                    <input
                        type="range"
                        v-model.number="collectCount"
                        :min="1"
                        :max="budget.max_leads"
                        class="flex-1 h-1.5 rounded-full appearance-none bg-gray-200 accent-brandPrimary"
                    />
                    <span class="text-sm font-semibold text-gray-900 w-10 text-right">{{ collectCount }}</span>
                </div>
                <div class="rounded-md bg-amber-50 border border-amber-100 px-2 py-1.5 text-[10px] text-amber-700 mb-2">
                    <i class="fa-regular fa-triangle-exclamation mr-1"></i>
                    {{ _mft('lead-hunter:scout.batchSizeWarning') }}
                </div>
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                        <span class="text-[10px] text-gray-500">
                            <container-with-tooltip additional-tooltip-class="w-56">
                                {{ _mft('lead-hunter:scout.usagePercentOfBudget', { usagePercent }) }}

                                <template #tooltip>
                                    {{ _mft('lead-hunter:scout.usageBudgetTooltip', { collectCount, usagePercent, includedLeads: budget.included_leads, billingPlan: budget.billing_plan }) }}
                                </template>
                            </container-with-tooltip>
                        </span>
                        <span v-if="estimatedTime" class="text-[10px] text-gray-400">
                            <i class="fa-regular fa-clock mr-0.5"></i>
                            ~{{ estimatedTime }}
                        </span>
                    </div>
                    <button-primary
                        @click="collectLeads"
                        :disabled="collecting"
                        size="sm"
                    >
                        <mf-spinner-medium v-if="collecting" class="w-6 h-3" />
                        <template v-else>
                            {{ _mft('lead-hunter:scout.collectNow') }} ({{ collectCount }})
                        </template>
                    </button-primary>
                </div>
            </template>

            <!-- Subsequent: collect more -->
            <template v-else-if="pendingResults === 0">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                        <span class="text-[10px] text-gray-500">
                            <container-with-tooltip additional-tooltip-class="w-48">
                                {{ budgetPercent }}% {{ _mft('lead-hunter:scout.budgetLeft') }}

                                <template #tooltip>
                                    {{ _mft('lead-hunter:scout.budgetTooltip', { acquiredLeadsCount, includedLeads: budget.included_leads, billingPlan: budget.billing_plan }) }}
                                </template>
                            </container-with-tooltip>
                        </span>
                        <span v-if="estimatedTime" class="text-[10px] text-gray-400">
                            <i class="fa-regular fa-clock mr-0.5"></i>
                            ~{{ estimatedTime }}
                        </span>
                    </div>
                    <button-primary
                        @click="collectLeads"
                        :disabled="collecting"
                        size="sm"
                    >
                        <mf-spinner-medium v-if="collecting" class="w-6 h-3" />
                        <template v-else>
                            {{ _mft('lead-hunter:scout.collectMore') }}
                        </template>
                    </button-primary>
                </div>
            </template>

            <!-- Collecting in progress -->
            <template v-else>
                <div class="flex items-center gap-2 text-xs text-amber-600">
                    <mf-spinner-medium class="w-6 h-3" />
                    {{ _mft('lead-hunter:scout.moreLeadsComing') }}
                </div>
            </template>
        </div>

        <!-- Filter bar -->
        <segmented-filter
            v-model="filter"
            :items="filterItems"
            aria-label="Lead filters"
        />

        <!-- Grid -->
        <div class="flex-1 overflow-y-auto p-4">
            <div v-if="loading" class="flex items-center justify-center h-32">
                <mf-spinner-medium class="w-10 h-6" />
            </div>

            <div v-else-if="filteredLeads.length === 0" class="flex items-center justify-center h-32 text-sm text-gray-400">
                {{ filter === 'all' ? _mft('lead-hunter:scout.noLeadsYet') : _mft('lead-hunter:scout.noLeadsInFilter') }}
            </div>

            <div v-else class="grid lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 max-w-7xl mx-auto">
                <scout-lead-card
                    v-for="lead in filteredLeads"
                    :key="lead.id"
                    :lead="lead"
                    :search-id="searchId"
                    @added="onLeadAdded"
                    @show-insights="showInsights"
                    @hide="hideLead"
                />
            </div>
        </div>

        <!-- Insights slideout -->
        <add-insights-domain-with-parser
            v-if="insightsDomain || insightsEmail"
            :domain="insightsDomain"
            :email="insightsEmail"
            @close="closeInsights"
        />
    </div>
</template>
